import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wallet, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Building2, Coins, BarChart3 } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import { useBankingStore } from '../store/bankingStore'
import { useThemeStore } from '../store/themeStore'
import { formatCurrency } from '../lib/utils'
import BankLogo from './BankLogo'

const MASK = '••••••'

interface NetWorthModalProps {
  isOpen: boolean
  onClose: () => void
}

const NetWorthModal: React.FC<NetWorthModalProps> = ({ isOpen, onClose }) => {
  const { portfolio, getCurrentPrice, convertPrice, displayCurrency, exchangeRate } = usePortfolioStore()
  const { accounts, loaded: bankLoaded, loadAccounts } = useBankingStore()
  const { isDark, maskAmounts } = useThemeStore()

  const [showBankDetail, setShowBankDetail] = useState(false)
  const [showInvestDetail, setShowInvestDetail] = useState(false)

  const m = (v: string) => (maskAmounts ? MASK : v)

  useEffect(() => { if (!bankLoaded) loadAccounts() }, [bankLoaded])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // ── Investments (non-gold) ──────────────────────────────────────────────────
  const investments = useMemo(() => {
    const holdings = portfolio.filter(h => h.port_api_type !== 'gold')
    let value = 0, cost = 0
    holdings.forEach(h => {
      value += getCurrentPrice(h.symbol, displayCurrency) * h.shares
      cost += convertPrice(h.purchase_price, h.purchase_currency, displayCurrency) * h.shares
    })
    return { value, cost, gainLoss: value - cost, count: holdings.length }
  }, [portfolio, getCurrentPrice, convertPrice, displayCurrency, exchangeRate])

  // ── Gold ────────────────────────────────────────────────────────────────────
  const gold = useMemo(() => {
    const holdings = portfolio.filter(h => h.port_api_type === 'gold')
    let value = 0, cost = 0
    holdings.forEach(h => {
      const priceThb = getCurrentPrice(h.symbol, 'THB')
      value += convertPrice(priceThb * h.shares, 'THB', displayCurrency)
      cost += convertPrice(h.purchase_price * h.shares, 'THB', displayCurrency)
    })
    return { value, cost, gainLoss: value - cost, count: holdings.length }
  }, [portfolio, getCurrentPrice, convertPrice, displayCurrency, exchangeRate])

  // ── Banking ─────────────────────────────────────────────────────────────────
  const banking = useMemo(() => {
    const total = accounts.reduce((sum, acc) => {
      const cur = (acc.currency === 'USD' || acc.currency === 'THB') ? acc.currency : 'THB'
      return sum + convertPrice(acc.balance, cur, displayCurrency)
    }, 0)
    return { value: total, count: accounts.length }
  }, [accounts, convertPrice, displayCurrency, exchangeRate])

  const netWorth = investments.value + gold.value + banking.value
  const totalCost = investments.cost + gold.cost
  const totalGainLoss = investments.gainLoss + gold.gainLoss
  const totalGainLossPct = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0
  const isGain = totalGainLoss >= 0

  const pct = (part: number) => netWorth > 0 ? (part / netWorth) * 100 : 0

  const categories = [
    {
      key: 'invest', label: 'การลงทุน', icon: <BarChart3 className="w-4 h-4" />,
      value: investments.value, cost: investments.cost, gainLoss: investments.gainLoss,
      pct: pct(investments.value), barColor: 'bg-indigo-500',
      bgLight: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400',
      count: `${investments.count} หลักทรัพย์`,
    },
    {
      key: 'gold', label: 'ทองคำ', icon: <Coins className="w-4 h-4" />,
      value: gold.value, cost: gold.cost, gainLoss: gold.gainLoss,
      pct: pct(gold.value), barColor: 'bg-amber-500',
      bgLight: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400',
      count: `${gold.count} รายการ`,
    },
    {
      key: 'bank', label: 'เงินฝากธนาคาร', icon: <Building2 className="w-4 h-4" />,
      value: banking.value, cost: null, gainLoss: null,
      pct: pct(banking.value), barColor: 'bg-teal-500',
      bgLight: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-600 dark:text-teal-400',
      count: `${banking.count} บัญชี`,
    },
  ]

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="sheet fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose() }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
          className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Modal header ─────────────────────────────────────────── */}
          <div className={`sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b ${isDark ? 'bg-gray-900 border-gray-700/50' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-brand-soft">
                <Wallet className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900 dark:text-white">Net Worth</p>
                <p className="text-xs text-gray-400">ภาพรวม wealth ทั้งหมด</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ── Total Net Worth ───────────────────────────────────────── */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-end justify-between mb-1">
              <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                {m(formatCurrency(netWorth, displayCurrency))}
              </p>
              {totalGainLoss !== 0 && (
                <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold mb-1 ${
                  isGain ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                         : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {isGain ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {m(`${isGain ? '+' : ''}${totalGainLossPct.toFixed(2)}%`)}
                </div>
              )}
            </div>
            {totalCost > 0 && (
              <p className={`text-sm ${isGain ? 'text-gain' : 'text-loss'}`}>
                {m(`${isGain ? '+' : ''}${formatCurrency(Math.abs(totalGainLoss), displayCurrency)}`)}
                <span className="text-gray-400 dark:text-gray-400 font-normal ml-1.5">unrealized P&L</span>
              </p>
            )}

            {/* Stacked allocation bar */}
            <div className="mt-4 flex h-3 rounded-full overflow-hidden gap-0.5">
              {categories.map(cat => cat.pct > 0 && (
                <motion.div
                  key={cat.key}
                  initial={{ width: 0 }}
                  animate={{ width: `${cat.pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 }}
                  className={`${cat.barColor} rounded-full`}
                  title={`${cat.label}: ${cat.pct.toFixed(1)}%`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {categories.map(cat => cat.value > 0 && (
                <div key={cat.key} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${cat.barColor}`} />
                  <span className="text-xs text-gray-500 dark:text-gray-400">{cat.label} {cat.pct.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Category cards ────────────────────────────────────────── */}
          <div className={`grid grid-cols-1 sm:grid-cols-3 border-t ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
            {categories.map((cat, i) => (
              <div key={cat.key} className={`px-5 py-4 ${i < 2 ? `sm:border-r border-b sm:border-b-0 ${isDark ? 'border-gray-700/50' : 'border-gray-100'}` : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-1.5 rounded-lg ${cat.bgLight}`}>
                    <span className={cat.text}>{cat.icon}</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{cat.label}</p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-400">{cat.count}</p>
                  </div>
                  <span className={`ml-auto text-xs font-semibold ${cat.text}`}>{cat.pct.toFixed(1)}%</span>
                </div>
                <p className={`text-lg font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {m(formatCurrency(cat.value, displayCurrency))}
                </p>
                {cat.gainLoss != null && cat.cost != null && cat.cost > 0 && (
                  <p className={`text-xs mt-0.5 tabular-nums ${cat.gainLoss >= 0 ? 'text-gain' : 'text-loss'}`}>
                    {m(`${cat.gainLoss >= 0 ? '+' : ''}${formatCurrency(Math.abs(cat.gainLoss), displayCurrency)}`)}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* ── Bank accounts ─────────────────────────────────────────── */}
          {accounts.length > 0 && (
            <div className={`border-t ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
              <button
                onClick={() => setShowBankDetail(v => !v)}
                className={`w-full flex items-center justify-between px-6 py-3.5 transition-colors ${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}
              >
                <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Building2 className="w-4 h-4 text-teal-500" />
                  บัญชีธนาคาร ({accounts.length})
                </span>
                {showBankDetail ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showBankDetail && (
                <div className={`divide-y ${isDark ? 'divide-gray-700/40' : 'divide-gray-100'}`}>
                  {accounts.map(acc => {
                    const cur = (acc.currency === 'USD' || acc.currency === 'THB') ? acc.currency : 'THB'
                    const val = convertPrice(acc.balance, cur, displayCurrency)
                    return (
                      <div key={acc.id} className={`flex items-center justify-between px-6 py-3 ${isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/60'} transition-colors`}>
                        <div className="flex items-center gap-3">
                          <BankLogo bankName={acc.bank_name} size={28} />
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{acc.account_name}</p>
                            <p className="text-xs text-gray-400">{acc.bank_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold tabular-nums text-teal-600 dark:text-teal-400">
                            {m(formatCurrency(val, displayCurrency))}
                          </p>
                          {acc.interest_rate > 0 && (
                            <p className="text-xs text-gray-400">{acc.interest_rate}% ดอกเบี้ย</p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Investment breakdown ──────────────────────────────────── */}
          {investments.count > 0 && (
            <div className={`border-t ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
              <button
                onClick={() => setShowInvestDetail(v => !v)}
                className={`w-full flex items-center justify-between px-6 py-3.5 transition-colors ${isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}
              >
                <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <BarChart3 className="w-4 h-4 text-indigo-500" />
                  หลักทรัพย์ ({investments.count})
                </span>
                {showInvestDetail ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showInvestDetail && (
                <div className={`divide-y ${isDark ? 'divide-gray-700/40' : 'divide-gray-100'}`}>
                  {portfolio
                    .filter(h => h.port_api_type !== 'gold')
                    .map(h => {
                      const val = getCurrentPrice(h.symbol, displayCurrency) * h.shares
                      const costVal = convertPrice(h.purchase_price, h.purchase_currency, displayCurrency) * h.shares
                      const gl = val - costVal
                      const glPct = costVal > 0 ? (gl / costVal) * 100 : 0
                      const pos = gl >= 0
                      return (
                        <div key={h.id} className={`flex items-center justify-between px-6 py-2.5 ${isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/60'} transition-colors`}>
                          <div>
                            <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{h.symbol}</p>
                            <p className="text-xs text-gray-400 max-w-[200px] truncate">{h.name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold tabular-nums text-indigo-600 dark:text-indigo-400">
                              {m(formatCurrency(val, displayCurrency))}
                            </p>
                            {costVal > 0 && (
                              <p className={`text-xs tabular-nums ${pos ? 'text-gain' : 'text-loss'}`}>
                                {m(`${pos ? '+' : ''}${glPct.toFixed(2)}%`)}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          )}

          {/* Bottom padding */}
          <div className="h-4" />
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default NetWorthModal
